import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import {
  Add as AddIcon,
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { fmt } from "../utils/financeFormat";
import pinPrompt from "../utils/pinPrompt";

/*
 * Deposit slips (Invoices → Deposits → Deposit slips).
 *
 * The rule the page enforces: a check goes to Labette only on a slip, and a slip can only be
 * built from money already recorded in JobQ. So the flow is: record each check (or pick the
 * ones already recorded), add any non-invoice money, print the slip, take slip + checks to the
 * bank. JobQ then matches the slip to the bank's deposit by itself (hourly Plaid sync) and
 * alerts Slack when a slip doesn't post or a check deposit shows up with no slip.
 * API: jobq-api main.py "DEPOSIT SLIPS" / deposit_slips.py.
 */

const cents = (v) => Math.round(Number(v || 0) * 100);
const keyOf = (p) => (p.kind === "payment_group" ? `g:${p.id}` : `p:${p.id}`);
// Date-only fields (deposit date, bank date): print the date as stored.
const fmtDay = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return `${m}/${d}/${y}`;
};
// Timestamps (recorded_at is UTC): the shop's local date, so a check recorded at 8 PM is still today.
const fmtLocalDay = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) : "";
// Pre-tick only what is plainly a new check waiting to go to the bank.
const needsALook = (p) => !!(p.already_on_slip || p.already_deposited_on || p.amount_changed);

function statusChip(slip) {
  if (slip.display_status === "void") return <Chip size="small" label="Void" variant="outlined" />;
  if (slip.display_status === "posted") {
    const when = slip.bank?.date ? ` ${fmtDay(slip.bank.date)}` : "";
    const partners = slip.bank?.combined_with || [];
    if (partners.length && !slip.recorded_after_bank) {
      return (
        <Tooltip title={`The teller put ${[slip.deposit_number, ...partners].join(" and ")} through as one ${fmt(slip.bank.amount)} deposit.`}>
          <Chip size="small" color="success" label={`Posted at bank${when} · with ${partners.join(" + ")}`} />
        </Tooltip>
      );
    }
    return slip.recorded_after_bank ? (
      <Tooltip title="The slip was made after Labette had already posted the deposit.">
        <Chip size="small" color="warning" label={`Posted${when} · slip made after`} />
      </Tooltip>
    ) : (
      <Chip size="small" color="success" label={`Posted at bank${when}`} />
    );
  }
  if (slip.display_status === "late") {
    return (
      <Tooltip title="Not in checking yet after 3 business days. If the checks went to the bank, call Labette: they may be in another account.">
        <Chip size="small" color="error" icon={<WarningIcon />} label="Not posted: check with Labette" />
      </Tooltip>
    );
  }
  return <Chip size="small" color="info" variant="outlined" label={`Waiting for bank (by ${fmtDay(slip.post_due_by)})`} />;
}

async function openSlipPdf(depositId, onShowMessage) {
  try {
    const res = await axios.get(`/deposits/${depositId}/slip.pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const win = window.open(url, "_blank");
    if (!win) onShowMessage?.("The browser blocked the pop-up. Use the Print button on the slip.", "warning");
  } catch (e) {
    let detail = null;
    try {
      detail = JSON.parse(await e.response?.data?.text())?.detail;
    } catch {
      /* not JSON */
    }
    onShowMessage?.(detail || "Could not open the deposit slip.", "error");
  }
}

// ─── Record a check (a batch payment: one check, one or more invoices) ───────────────

function RecordCheckDialog({ open, onClose, onRecorded, customers, onShowMessage, today }) {
  const [customer, setCustomer] = useState(null);
  const [amount, setAmount] = useState("");
  const [checkNumber, setCheckNumber] = useState("");
  const [method, setMethod] = useState("Check");
  const [paymentDate, setPaymentDate] = useState(today || "");
  const [memo, setMemo] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [alloc, setAlloc] = useState({});
  const [overageToCredit, setOverageToCredit] = useState(false);
  const [loadingInv, setLoadingInv] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCustomer(null);
    setAmount("");
    setCheckNumber("");
    setMethod("Check");
    setPaymentDate(today || "");
    setMemo("");
    setInvoices([]);
    setAlloc({});
    setOverageToCredit(false);
  }, [open, today]);

  const loadInvoices = async (cust, amt) => {
    if (!cust) { setInvoices([]); setAlloc({}); return; }
    setLoadingInv(true);
    try {
      const res = await axios.get("/invoices", { params: { customer_id: cust.customer_id, status: "Open" } });
      setInvoices(res.data || []);
      if (Number(amt) > 0) {
        const auto = await axios.get("/payments/auto-allocate", {
          params: { customer_id: cust.customer_id, amount: Number(amt) },
        });
        const next = {};
        (auto.data?.proposed_allocations || []).forEach((a) => {
          if (a.allocation > 0) next[a.invoice_id] = a.allocation;
        });
        setAlloc(next);
      } else {
        setAlloc({});
      }
    } catch (e) {
      setInvoices([]);
      setAlloc({});
    }
    setLoadingInv(false);
  };

  const allocCents = Object.values(alloc).reduce((s, v) => s + cents(v), 0);
  const totalCents = cents(amount);
  const overCents = totalCents - allocCents;

  const save = async () => {
    if (!customer) { onShowMessage?.("Pick the customer.", "warning"); return; }
    if (totalCents <= 0) { onShowMessage?.("Enter the check amount.", "warning"); return; }
    const allocations = Object.entries(alloc)
      .filter(([, v]) => cents(v) > 0)
      .map(([invoiceId, v]) => ({ invoice_id: Number(invoiceId), amount: cents(v) / 100 }));
    if (!allocations.length) {
      onShowMessage?.("Apply the check to at least one invoice. Money for no invoice goes under “Other money”.", "warning");
      return;
    }
    if (overCents < 0) { onShowMessage?.("The invoices add up to more than the check.", "error"); return; }
    if (overCents > 0 && !overageToCredit) {
      onShowMessage?.(`The check is ${fmt(overCents / 100)} more than the invoices. Tick “hold the extra as a credit” or fix the amounts.`, "warning");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post("/payments/batch", {
        customer_id: customer.customer_id,
        total_amount: totalCents / 100,
        payment_date: paymentDate || null,
        payment_method: method,
        check_number: method === "Check" ? (checkNumber.trim() || null) : null,
        memo: memo.trim() || null,
        allocations,
        ...(overCents > 0 ? { overage_disposition: "credit" } : {}),
      });
      const errs = (res.data?.allocations || []).filter((a) => a.sync_error);
      onShowMessage?.(
        errs.length
          ? `Recorded, but Bill.com didn't take ${errs.length} of the invoice payments. Mike will need to fix Bill.com.`
          : `Recorded ${customer.name} ${method === "Check" && checkNumber ? `#${checkNumber} ` : ""}${fmt(totalCents / 100)}.`,
        errs.length ? "warning" : "success"
      );
      onRecorded?.(res.data?.payment_group_id);
    } catch (e) {
      onShowMessage?.(e.response?.data?.detail || "Could not record the payment.", "error");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Record a check</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 1 }}>
          <Autocomplete
            sx={{ flex: "2 1 260px" }}
            options={customers}
            getOptionLabel={(c) => c?.name || ""}
            isOptionEqualToValue={(a, b) => a.customer_id === b.customer_id}
            value={customer}
            onChange={(_, c) => { setCustomer(c); loadInvoices(c, amount); }}
            renderInput={(params) => <TextField {...params} label="From (customer)" size="small" autoFocus />}
          />
          <TextField
            sx={{ flex: "1 1 140px" }} size="small" label="Amount" type="number" value={amount}
            onChange={(e) => setAmount(e.target.value)}
            onBlur={() => customer && loadInvoices(customer, amount)}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          />
          <FormControl size="small" sx={{ flex: "0 1 120px" }}>
            <InputLabel id="dep-check-method-label">Paid by</InputLabel>
            <Select labelId="dep-check-method-label" label="Paid by" value={method} onChange={(e) => setMethod(e.target.value)}>
              <MenuItem value="Check">Check</MenuItem>
              <MenuItem value="Cash">Cash</MenuItem>
            </Select>
          </FormControl>
          {method === "Check" && (
            <TextField sx={{ flex: "1 1 120px" }} size="small" label="Check #" value={checkNumber}
                       onChange={(e) => setCheckNumber(e.target.value)} />
          )}
          <TextField sx={{ flex: "1 1 150px" }} size="small" label="Date received" type="date" value={paymentDate}
                     onChange={(e) => setPaymentDate(e.target.value)} InputLabelProps={{ shrink: true }} />
        </Box>
        <TextField fullWidth size="small" label="Memo (optional)" value={memo} sx={{ mt: 1.5 }}
                   onChange={(e) => setMemo(e.target.value)} />

        {customer && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
              <Typography variant="subtitle2">Open invoices it pays</Typography>
              <Button size="small" disabled={!totalCents || loadingInv} onClick={() => loadInvoices(customer, amount)}>
                Auto-apply oldest first
              </Button>
            </Box>
            {loadingInv ? <CircularProgress size={20} /> : invoices.length === 0 ? (
              <Alert severity="info">
                {customer.name} has no open invoices. If this is money ahead of an invoice (a prepayment),
                close this and use “Other money” → Customer prepayment: it's held as their credit.
              </Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice</TableCell>
                    <TableCell>Due</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell align="right" sx={{ width: 150 }}>Apply</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((inv) => {
                    const balance = Number(inv.total || 0) - Number(inv.total_paid || 0);
                    return (
                      <TableRow key={inv.invoice_id}>
                        <TableCell>#{inv.invoice_number}</TableCell>
                        <TableCell>{fmtDay(inv.due_date)}</TableCell>
                        <TableCell align="right">{fmt(balance)}</TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small" type="number" value={alloc[inv.invoice_id] ?? ""}
                            onChange={(e) => setAlloc({ ...alloc, [inv.invoice_id]: e.target.value })}
                            inputProps={{ style: { textAlign: "right" } }}
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
            <Box sx={{ mt: 1, fontSize: 13 }}>
              Applied <b>{fmt(allocCents / 100)}</b> of <b>{fmt(totalCents / 100)}</b>
              {overCents > 0 && (
                <FormControlLabel
                  sx={{ ml: 1 }}
                  control={<Checkbox size="small" checked={overageToCredit} onChange={(e) => setOverageToCredit(e.target.checked)} />}
                  label={`Hold the extra ${fmt(overCents / 100)} as a customer credit`}
                />
              )}
              {overCents < 0 && <Typography color="error" variant="caption" sx={{ ml: 1 }}>More than the check.</Typography>}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? "Recording…" : "Record it"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Other money (scrap, a refund, a customer prepayment…) ──────────────────────────

function OtherMoneyDialog({ open, onClose, onAdd, customers, categories }) {
  const [category, setCategory] = useState("scrap_sale");
  const [tender, setTender] = useState("check");
  const [payer, setPayer] = useState("");
  const [customer, setCustomer] = useState(null);
  const [checkNumber, setCheckNumber] = useState("");
  const [amount, setAmount] = useState("");
  const [memo, setMemo] = useState("");

  useEffect(() => {
    if (!open) return;
    setCategory("scrap_sale");
    setTender("check");
    setPayer("");
    setCustomer(null);
    setCheckNumber("");
    setAmount("");
    setMemo("");
  }, [open]);

  const isPrepay = category === "customer_prepayment";
  const payerName = payer.trim() || (customer ? customer.name : "");
  const problem =
    cents(amount) <= 0 ? "Enter the amount."
      : isPrepay && !customer ? "Pick the customer: a prepayment is held as their credit."
        : !payerName ? "Who is it from?"
          : category === "other" && !memo.trim() ? "Say what it's for."
            : null;

  const add = () => {
    if (problem) return;
    onAdd({
      category,
      tender,
      payer_name: payerName,
      customer_id: customer ? customer.customer_id : null,
      check_number: tender === "check" ? (checkNumber.trim() || null) : null,
      amount: cents(amount) / 100,
      memo: memo.trim() || null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Other money on this deposit</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
          Money that doesn't pay an invoice still goes on the slip, so every check at the bank is in JobQ.
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ flex: "2 1 220px" }}>
            <InputLabel id="dep-other-category-label">What is it?</InputLabel>
            <Select labelId="dep-other-category-label" label="What is it?" value={category}
                    onChange={(e) => setCategory(e.target.value)}>
              {(categories || []).map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex: "1 1 110px" }}>
            <InputLabel id="dep-other-tender-label">Paid by</InputLabel>
            <Select labelId="dep-other-tender-label" label="Paid by" value={tender} onChange={(e) => setTender(e.target.value)}>
              <MenuItem value="check">Check</MenuItem>
              <MenuItem value="cash">Cash</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Autocomplete
          sx={{ mt: 1.5 }}
          options={customers}
          getOptionLabel={(c) => c?.name || ""}
          isOptionEqualToValue={(a, b) => a.customer_id === b.customer_id}
          value={customer}
          onChange={(_, c) => setCustomer(c)}
          renderInput={(params) => (
            <TextField {...params} size="small"
                       label={isPrepay ? "Customer (required)" : "Customer (if it's a customer)"} />
          )}
        />
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 1.5 }}>
          <TextField sx={{ flex: "2 1 200px" }} size="small" label="From (as on the check)" value={payer}
                     placeholder={customer ? customer.name : ""} onChange={(e) => setPayer(e.target.value)} />
          {tender === "check" && (
            <TextField sx={{ flex: "1 1 110px" }} size="small" label="Check #" value={checkNumber}
                       onChange={(e) => setCheckNumber(e.target.value)} />
          )}
          <TextField sx={{ flex: "1 1 130px" }} size="small" label="Amount" type="number" value={amount}
                     onChange={(e) => setAmount(e.target.value)}
                     InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
        </Box>
        <TextField fullWidth size="small" sx={{ mt: 1.5 }} value={memo} onChange={(e) => setMemo(e.target.value)}
                   label={category === "other" ? "What it's for (required)" : "Memo (optional)"} />
        {isPrepay && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            Held as an open credit for {customer ? customer.name : "the customer"}. Apply it to their invoice
            from Invoices → Credits when the invoice exists.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Tooltip title={problem || ""}>
          <span><Button variant="contained" onClick={add} disabled={!!problem}>Add to slip</Button></span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}

// ─── Match a bank deposit to slips already printed (the teller combined them) ───────
// Labette sometimes takes two trips' slips in as ONE deposit (DEP-0004 $500.00 + DEP-0005
// $1,759.34 posted as one $2,259.34 Regular Deposit on 10/09/2026). JobQ matches that on its own
// only when exactly one set of open slips adds up to it; otherwise a person ticks them here.

function MatchSlipsDialog({ txn, openSlips, onClose, onMatched, onShowMessage }) {
  const [picked, setPicked] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!txn) return;
    const next = {};
    (txn.suggested_deposit_ids || []).forEach((id) => { next[id] = true; });
    setPicked(next);
  }, [txn]);

  const slips = openSlips || [];
  const chosen = slips.filter((s) => picked[s.deposit_id]);
  const totalCents = chosen.reduce((sum, s) => sum + s.total_cents, 0);
  const diff = txn ? totalCents - txn.amount_cents : 0;
  const numbers = chosen.map((s) => s.deposit_number).join(" + ");

  const save = async () => {
    if (!chosen.length || diff !== 0) return;
    const pin = await pinPrompt(`Enter your PIN to tie ${numbers} to Labette's ${fmt(txn.amount)} deposit of ${fmtDay(txn.date)}:`, { face: false });
    if (!pin) return;
    setSaving(true);
    try {
      await axios.post("/deposits/match-bank", {
        pin, bank_transaction_id: txn.id, deposit_ids: chosen.map((s) => s.deposit_id),
      });
      onShowMessage?.(`${numbers} tied to Labette's ${fmt(txn.amount)} deposit of ${fmtDay(txn.date)}.`, "success");
      onMatched?.();
    } catch (e) {
      onShowMessage?.(e.response?.data?.detail || "Could not match the slips.", "error");
      // 409: JobQ's hourly match (or someone else) got there first; show the page as it is now
      if (e.response?.status === 409) onMatched?.();
    }
    setSaving(false);
  };

  return (
    <Dialog open={!!txn} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Match {txn ? `Labette's ${fmt(txn.amount)} deposit of ${fmtDay(txn.date)}` : ""} to slips</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
          The teller sometimes puts two slips through as one deposit. Tick the slips that were in it: they have to
          add up to the deposit exactly.
        </Typography>
        {txn?.auto_match_blocked && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            An Admin undid a slip match on this deposit. Only an Admin can match it to slips again.
          </Alert>
        )}
        {slips.length === 0 ? (
          <Alert severity="info">No slips are waiting for the bank. Build a slip for this deposit instead.</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Slip</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Checks</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {slips.map((s) => (
                <TableRow key={s.deposit_id} hover selected={!!picked[s.deposit_id]}>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={!!picked[s.deposit_id]}
                              inputProps={{ "aria-label": `Include ${s.deposit_number}` }}
                              onChange={(e) => setPicked({ ...picked, [s.deposit_id]: e.target.checked })} />
                  </TableCell>
                  <TableCell><b>{s.deposit_number}</b></TableCell>
                  <TableCell>{fmtDay(s.deposit_date)}</TableCell>
                  <TableCell>{(s.payers || []).join(", ")}</TableCell>
                  <TableCell align="right">{fmt(s.total_amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {txn && (
          <Box sx={{ mt: 1.5, fontSize: 14 }}>
            Ticked <b>{fmt(totalCents / 100)}</b> of the bank's <b>{fmt(txn.amount)}</b>
            {chosen.length > 0 && diff !== 0 && (
              <span style={{ color: "#ef4444", marginLeft: 8 }}>
                {fmt(Math.abs(diff) / 100)} {diff > 0 ? "over" : "short"}
              </span>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving || !chosen.length || diff !== 0}>
          {saving ? "Matching…" : chosen.length > 1 ? `Tie ${chosen.length} slips to this deposit` : "Tie to this deposit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── One slip's details (items, what they paid, bank match) ─────────────────────────

function SlipDetails({ depositId, onShowMessage, onChanged, border }) {
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [undoOpen, setUndoOpen] = useState(false);
  const [undoReason, setUndoReason] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`/deposits/${depositId}`);
      setDetail(res.data);
    } catch (e) {
      onShowMessage?.(e.response?.data?.detail || "Could not load the slip.", "error");
    }
  }, [depositId, onShowMessage]);

  useEffect(() => { load(); }, [load]);

  const match = async (txn) => {
    // with_slips: the other open slips the teller put through with this one (one $X deposit)
    const others = txn.exact ? [] : (txn.with_slips || []);
    const numbers = [detail.deposit_number, ...others.map((s) => s.deposit_number)].join(" + ");
    const pin = await pinPrompt(`Enter your PIN to tie ${numbers} to Labette's ${fmt(txn.amount)} deposit of ${fmtDay(txn.date)}:`, { face: false });
    if (!pin) return;
    setBusy(true);
    try {
      if (others.length) {
        await axios.post("/deposits/match-bank", {
          pin, bank_transaction_id: txn.id, deposit_ids: [depositId, ...others.map((s) => s.deposit_id)],
        });
      } else {
        await axios.post(`/deposits/${depositId}/match`, { pin, bank_transaction_id: txn.id });
      }
      onShowMessage?.(`${numbers} tied to the bank deposit.`, "success");
      await load();
      onChanged?.();
    } catch (e) {
      onShowMessage?.(e.response?.data?.detail || "Could not match.", "error");
      if (e.response?.status === 409) {
        await load();
        onChanged?.();
      }
    }
    setBusy(false);
  };

  const undoNumbers = detail ? [detail.deposit_number, ...(detail.bank?.combined_with || [])] : [];

  const undo = async () => {
    const pin = await pinPrompt(`Admin PIN to undo the bank match of ${undoNumbers.join(" + ")}:`, { autofill: false });
    if (!pin) return;
    setBusy(true);
    try {
      await axios.post(`/deposits/${depositId}/unmatch`, { pin, reason: undoReason.trim() });
      onShowMessage?.(`${undoNumbers.join(" + ")} untied from the bank deposit. ${undoNumbers.length > 1 ? "They wait" : "It waits"} for the bank again.`, "success");
      setUndoOpen(false);
      await load();
      onChanged?.();
    } catch (e) {
      onShowMessage?.(e.response?.data?.detail || "Could not undo the match.", "error");
      if (e.response?.status === 409) {
        setUndoOpen(false);
        await load();
        onChanged?.();
      }
    }
    setBusy(false);
  };

  if (!detail) return <Box sx={{ p: 2 }}><CircularProgress size={20} /></Box>;
  return (
    <Box sx={{ p: 1.5, borderTop: `1px solid ${border}` }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>From</TableCell>
            <TableCell>Check #</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell>Applied to</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {detail.items.map((it) => (
            <TableRow key={it.deposit_item_id}>
              <TableCell>
                {it.payer_name}{it.tender === "cash" ? " (cash)" : ""}
                {it.category_label && <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                  {it.category_label}{it.memo ? `: ${it.memo}` : ""}
                </Typography>}
              </TableCell>
              <TableCell>{it.check_number || "—"}</TableCell>
              <TableCell align="right">{fmt(it.amount)}</TableCell>
              <TableCell>
                {(it.allocations || []).map((a, i) => (
                  <Chip key={i} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }}
                        label={`${a.label}${a.amount_cents != null && it.allocations.length > 1 ? ` ${fmt(a.amount_cents / 100)}` : ""}${a.status ? ` · ${a.status}` : ""}`} />
                ))}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {detail.notes && <Typography variant="caption" sx={{ display: "block", mt: 1 }}>Note: {detail.notes}</Typography>}
      {detail.status === "void" && (
        <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
          Voided by {detail.voided_by}: {detail.void_reason}
        </Typography>
      )}
      {detail.bank && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, flexWrap: "wrap" }}>
          <Typography variant="caption">
            Bank: {fmt(detail.bank.amount)} on {fmtDay(detail.bank.date)} ({detail.bank.label})
            {detail.bank.combined_with?.length ? `, one deposit together with ${detail.bank.combined_with.join(" + ")}` : ""}
            {" "}· matched by {detail.matched_by}
          </Typography>
          {detail.can_unmatch && (
            <Button size="small" color="warning" onClick={() => { setUndoReason(""); setUndoOpen(true); }} disabled={busy}>
              Undo bank match
            </Button>
          )}
        </Box>
      )}
      {(detail.unmatches || []).map((u, i) => (
        <Typography key={i} variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
          Bank match undone {fmtLocalDay(u.at)} by {u.unmatched_by}
          {u.bank_amount != null ? ` (${fmt(u.bank_amount)} deposit of ${fmtDay(u.bank_date)})` : ""}: {u.reason}
        </Typography>
      ))}
      <Dialog open={undoOpen} onClose={busy ? undefined : () => setUndoOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Undo the bank match?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {undoNumbers.join(" + ")} will no longer be tied to Labette's {detail.bank ? fmt(detail.bank.amount) : ""} deposit
            of {detail.bank ? fmtDay(detail.bank.date) : ""}. {undoNumbers.length > 1 ? "They go" : "It goes"} back to
            waiting for the bank, the payments lose their bank link, and JobQ won't match that deposit to slips on its
            own again (you can still match it by hand). Admin only.
          </Typography>
          <TextField fullWidth size="small" label="Why is the match wrong?" value={undoReason} autoFocus
                     onChange={(e) => setUndoReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUndoOpen(false)} disabled={busy}>Keep it</Button>
          <Button color="warning" variant="contained" onClick={undo} disabled={busy || undoReason.trim().length < 5}>
            Undo match
          </Button>
        </DialogActions>
      </Dialog>
      {detail.status === "printed" && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="subtitle2">Labette deposits it could be</Typography>
          {detail.bank_candidates.length === 0 ? (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              None yet. JobQ checks every hour and matches an exact {fmt(detail.total_amount)} deposit on its own.
            </Typography>
          ) : detail.bank_candidates.map((t) => (
            <Box key={t.id} sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
              <Typography variant="body2">{fmtDay(t.date)} · {fmt(t.amount)} · {t.label}</Typography>
              {t.auto_match_blocked && (
                <Tooltip title="An Admin undid a slip match on this deposit. Only an Admin can match it by hand.">
                  <Chip size="small" variant="outlined" color="warning" label="Match was undone (Admin only)" />
                </Tooltip>
              )}
              {t.exact ? (
                <Button size="small" variant="outlined" disabled={busy || t.pending} onClick={() => match(t)}>
                  This is it
                </Button>
              ) : t.with_slips?.length ? (
                <Tooltip title={`The teller may have put ${[detail.deposit_number, ...t.with_slips.map((s) => s.deposit_number)].join(" and ")} through as one deposit: together they add up to ${fmt(t.amount)} exactly.`}>
                  <span>
                    <Button size="small" variant="outlined" disabled={busy || t.pending} onClick={() => match(t)}>
                      This is it, with {t.with_slips.map((s) => s.deposit_number).join(" + ")}
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <Chip size="small" label={`${fmt(Math.abs(t.amount - detail.total_amount))} off`} />
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ─── The page ────────────────────────────────────────────────────────────────────────

export default function DepositSlipsView({ isDark, onShowMessage }) {
  const [onHand, setOnHand] = useState(null);
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [others, setOthers] = useState([]);
  const [depositDate, setDepositDate] = useState("");
  const [notes, setNotes] = useState("");
  const [catchUp, setCatchUp] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [recordOpen, setRecordOpen] = useState(false);
  const [otherOpen, setOtherOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [dismissTarget, setDismissTarget] = useState(null);
  const [dismissBy, setDismissBy] = useState("");
  const [dismissReason, setDismissReason] = useState("");
  const [matchTarget, setMatchTarget] = useState(null);
  const pendingSelect = useRef(null);
  const builderRef = useRef(null);
  const catchUpRef = useRef(null);

  const cardBg = isDark ? "#1e1e2d" : "#ffffff";
  const border = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const muted = isDark ? "#94a3b8" : "#64748b";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [oh, list] = await Promise.all([
        axios.get("/deposits/on-hand"),
        axios.get("/deposits", { params: { limit: 40 } }),
      ]);
      setOnHand(oh.data);
      setSlips(list.data?.data || []);
      setDepositDate((d) => d || oh.data?.today || "");
      setSelected((prev) => {
        const next = {};
        (oh.data?.payments || []).forEach((p) => {
          const k = keyOf(p);
          if (k in prev) next[k] = prev[k];
          else next[k] = !needsALook(p) && !catchUpRef.current;
        });
        if (pendingSelect.current) {
          next[`g:${pendingSelect.current}`] = true;
          pendingSelect.current = null;
        }
        return next;
      });
    } catch (e) {
      onShowMessage?.(e.response?.data?.detail || "Could not load deposit slips.", "error");
    }
    setLoading(false);
  }, [onShowMessage]);

  useEffect(() => { catchUpRef.current = catchUp; }, [catchUp]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get("/customers")
      .then((res) => setCustomers((res.data || []).map((c) => ({ customer_id: c.customer_id, name: c.name }))
        .sort((a, b) => a.name.localeCompare(b.name))))
      .catch(() => setCustomers([]));
  }, []);

  const payments = useMemo(() => onHand?.payments || [], [onHand]);
  const chosen = payments.filter((p) => selected[keyOf(p)]);
  const totalCents = chosen.reduce((s, p) => s + p.amount_cents, 0) + others.reduce((s, o) => s + cents(o.amount), 0);
  const itemCount = chosen.length + others.length;
  const account = onHand?.account;
  const numberMissing = account && !account.account_number_set;
  const catchUpDiff = catchUp ? totalCents - catchUp.amount_cents : 0;

  const create = async () => {
    if (!itemCount) { onShowMessage?.("Pick the checks going to the bank.", "warning"); return; }
    if (catchUp && catchUpDiff !== 0) {
      onShowMessage?.(`The slip must equal Labette's ${fmt(catchUp.amount)} deposit exactly (off by ${fmt(Math.abs(catchUpDiff) / 100)}).`, "warning");
      return;
    }
    const pin = await pinPrompt(catchUp ? "Enter your PIN to record this deposit's slip:" : "Enter your PIN to create the deposit slip:", { face: false });
    if (!pin) return;
    setCreating(true);
    try {
      const res = await axios.post("/deposits", {
        pin,
        deposit_date: catchUp ? null : (depositDate || null),
        payment_group_ids: chosen.filter((p) => p.kind === "payment_group").map((p) => p.id),
        invoice_payment_ids: chosen.filter((p) => p.kind === "invoice_payment").map((p) => p.id),
        other_items: others,
        notes: notes.trim() || null,
        bank_transaction_id: catchUp ? catchUp.id : null,
      });
      setJustCreated(res.data);
      setOthers([]);
      setNotes("");
      if (catchUp) {
        // back to the normal builder: its usual defaults apply again
        catchUpRef.current = null;
        setCatchUp(null);
        setSelected({});
      }
      onShowMessage?.(`${res.data.deposit_number} created: ${fmt(res.data.total_amount)}.`, "success");
      if (!catchUp) openSlipPdf(res.data.deposit_id, onShowMessage);
      await load();
    } catch (e) {
      onShowMessage?.(e.response?.data?.detail || "Could not create the slip.", "error");
    }
    setCreating(false);
  };

  const confirmVoid = async () => {
    if (voidReason.trim().length < 3) { onShowMessage?.("Say why.", "warning"); return; }
    const pin = await pinPrompt(`Enter your PIN to void ${voidTarget.deposit_number}:`, { face: false });
    if (!pin) return;
    try {
      await axios.post(`/deposits/${voidTarget.deposit_id}/void`, { pin, reason: voidReason.trim() });
      onShowMessage?.(`${voidTarget.deposit_number} voided. Its checks are back on hand.`, "success");
      setVoidTarget(null);
      setVoidReason("");
      if (justCreated?.deposit_id === voidTarget.deposit_id) setJustCreated(null);
      await load();
    } catch (e) {
      onShowMessage?.(e.response?.data?.detail || "Could not void the slip.", "error");
    }
  };

  const confirmDismiss = async () => {
    if (!dismissBy.trim()) { onShowMessage?.("Enter your name.", "warning"); return; }
    try {
      await axios.post(`/finance/unmatched-deposits/${dismissTarget.id}/dismiss`, {
        dismissed_by: dismissBy.trim(),
        reason: dismissReason.trim() || "Not customer money (deposit slips)",
      });
      onShowMessage?.("Marked as not customer money.", "success");
      setDismissTarget(null);
      setDismissReason("");
      await load();
    } catch (e) {
      onShowMessage?.(e.response?.data?.detail || "Could not update the bank deposit.", "error");
    }
  };

  const startCatchUp = (txn) => {
    setCatchUp(txn);
    setSelected({});
    setOthers([]);
    setJustCreated(null);
    setTimeout(() => builderRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" }), 50);
  };

  const bankNoSlip = onHand?.bank_deposits_without_slip || [];
  const openSlips = onHand?.open_slips || [];

  return (
    <Box sx={{ p: 2, overflowY: "auto", flex: 1 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, gap: 1, flexWrap: "wrap" }}>
        <Box sx={{ maxWidth: 760 }}>
          <Typography variant="h5">Deposit slips</Typography>
          <Typography variant="caption" sx={{ color: muted }}>
            Every check goes to the bank on a JobQ slip. Record the checks, print the slip, take both to Labette.
            JobQ matches the slip to the bank's deposit when it posts, and Slack hears about any deposit without one.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {account && (
            <Chip size="small" variant="outlined"
                  label={`${account.bank_name} · ${account.account_title} · checking ${account.account_masked}`} />
          )}
          <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            {loading ? "Loading…" : "Refresh"}
          </Button>
        </Box>
      </Box>

      {numberMissing && (
        <Alert severity="error" sx={{ mb: 2 }}>
          The {account.bank_name} account number isn't set up in JobQ, so no slip can print. Ask Mike.
        </Alert>
      )}

      {justCreated && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setJustCreated(null)}
               action={justCreated.status === "printed" ? (
                 <Button color="inherit" size="small" startIcon={<PrintIcon />}
                         onClick={() => openSlipPdf(justCreated.deposit_id, onShowMessage)}>Print slip</Button>
               ) : null}>
          <b>{justCreated.deposit_number}</b>: {justCreated.check_count} check{justCreated.check_count === 1 ? "" : "s"}
          {justCreated.cash_amount > 0 ? ` + ${fmt(justCreated.cash_amount)} cash` : ""}, {fmt(justCreated.total_amount)}.
          {justCreated.status === "printed"
            ? " Stamp each check “For deposit only”, then take the slip and the checks to the bank."
            : " Tied to the bank deposit."}
        </Alert>
      )}

      {bankNoSlip.length > 0 && (
        <Card sx={{ background: cardBg, border: "1px solid #ef4444", mb: 2 }}>
          <CardContent sx={{ pb: "12px !important" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Bank deposits with no slip</Typography>
            <Typography variant="caption" sx={{ color: muted, display: "block", mb: 1 }}>
              These reached Labette checking without a JobQ slip. If the teller put slips you already printed through
              as one deposit, match it to those slips. Otherwise record the checks that were in it and build its slip
              (the total has to match), or mark it as not customer money.
            </Typography>
            {bankNoSlip.map((t) => {
              const suggested = t.suggested_deposit_numbers || [];
              return (
                <Box key={t.id} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5, flexWrap: "wrap" }}>
                  <Typography variant="body2" sx={{ minWidth: 220 }}>
                    <b>{fmt(t.amount)}</b> · {fmtDay(t.date)} · {t.label}
                  </Typography>
                  {t.pending && <Chip size="small" color="warning" label="Pending at bank" />}
                  {t.auto_match_blocked && (
                    <Tooltip title="An Admin undid a slip match on this deposit, so JobQ won't match it on its own. Match it by hand, build its slip, or mark it not customer money.">
                      <Chip size="small" variant="outlined" label="Match was undone" />
                    </Tooltip>
                  )}
                  {suggested.length > 0 && (
                    <Tooltip title={`Open ${suggested.length > 1 ? "slips" : "slip"} ${suggested.join(" + ")} add${suggested.length > 1 ? "" : "s"} up to exactly ${fmt(t.amount)}.`}>
                      <Chip size="small" color="info" variant="outlined" label={`= ${suggested.join(" + ")}`} />
                    </Tooltip>
                  )}
                  {openSlips.length > 0 && (
                    <Button size="small" variant={suggested.length ? "contained" : "outlined"} disabled={t.pending}
                            onClick={() => setMatchTarget(t)}>
                      {suggested.length ? `Match to ${suggested.join(" + ")}` : "Match to slips…"}
                    </Button>
                  )}
                  <Button size="small" variant={suggested.length ? "outlined" : "contained"} disabled={t.pending}
                          onClick={() => startCatchUp(t)}>
                    Build slip for it
                  </Button>
                  <Button size="small" color="inherit" variant="outlined" onClick={() => { setDismissTarget(t); setDismissReason(""); }}>
                    Not customer money
                  </Button>
                </Box>
              );
            })}
          </CardContent>
        </Card>
      )}

      <Card ref={builderRef} sx={{ background: cardBg, border: `1px solid ${catchUp ? "#f59e0b" : border}`, mb: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {catchUp ? `Slip for Labette's ${fmt(catchUp.amount)} deposit of ${fmtDay(catchUp.date)}` : "New deposit slip"}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setRecordOpen(true)}>
                Record a check
              </Button>
              <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setOtherOpen(true)}>
                Other money
              </Button>
              {catchUp && <Button size="small" onClick={() => { setCatchUp(null); load(); }}>Cancel</Button>}
            </Box>
          </Box>
          {catchUp && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              This deposit already posted. Tick the checks that were in it (record any that aren't in JobQ yet).
              The slip must add up to exactly {fmt(catchUp.amount)}.
            </Alert>
          )}

          {payments.length === 0 && others.length === 0 ? (
            <Typography variant="body2" sx={{ color: muted, mt: 1.5 }}>
              No checks waiting. Record each check as it arrives (or on the invoice page), then build the slip here.
            </Typography>
          ) : (
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>From</TableCell>
                  <TableCell>Check #</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Pays</TableCell>
                  <TableCell>Recorded</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((p) => {
                  const k = keyOf(p);
                  return (
                    <TableRow key={k} hover selected={!!selected[k]}>
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={!!selected[k]}
                                  onChange={(e) => setSelected({ ...selected, [k]: e.target.checked })} />
                      </TableCell>
                      <TableCell>
                        {p.customer_name}{p.tender === "cash" ? " (cash)" : ""}
                        {p.already_on_slip && (
                          <Tooltip title={`A check with this number from this customer is already on ${p.already_on_slip}. Most likely it was re-recorded after that slip posted: don't deposit it twice.`}>
                            <Chip size="small" color="warning" icon={<WarningIcon />} sx={{ ml: 1 }} label={`Also on ${p.already_on_slip}`} />
                          </Tooltip>
                        )}
                        {!p.already_on_slip && p.already_deposited_on && (
                          <Tooltip title={`This customer's check with this number already went to the bank on ${fmtDay(p.already_deposited_on)}. Most likely this is a correction re-recorded afterwards: don't deposit it twice.`}>
                            <Chip size="small" color="warning" icon={<WarningIcon />} sx={{ ml: 1 }}
                                  label={`Deposited ${fmtDay(p.already_deposited_on)}`} />
                          </Tooltip>
                        )}
                        {p.amount_changed && (
                          <Tooltip title={`Recorded as ${fmt(p.recorded_amount)}; a payment on it was deleted or moved since. The slip uses what is left (${fmt(p.amount)}). Check it against the paper check.`}>
                            <Chip size="small" color="warning" icon={<WarningIcon />} sx={{ ml: 1 }}
                                  label={`Changed: was ${fmt(p.recorded_amount)}`} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>{p.check_number || <span style={{ color: muted }}>{p.memo ? `— (${p.memo})` : "—"}</span>}</TableCell>
                      <TableCell align="right">{fmt(p.amount)}</TableCell>
                      <TableCell>
                        {(p.allocations || []).map((a, i) => (
                          <Chip key={i} size="small" variant="outlined" sx={{ mr: 0.5 }} label={a.label} />
                        ))}
                      </TableCell>
                      <TableCell>{fmtLocalDay(p.recorded_at)}</TableCell>
                    </TableRow>
                  );
                })}
                {others.map((o, i) => (
                  <TableRow key={`o:${i}`} selected>
                    <TableCell padding="checkbox">
                      <IconButton size="small" onClick={() => setOthers(others.filter((_, j) => j !== i))}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      {o.payer_name}{o.tender === "cash" ? " (cash)" : ""}
                      <Typography variant="caption" sx={{ display: "block", color: muted }}>
                        {(onHand?.other_categories || []).find((c) => c.value === o.category)?.label || o.category}
                        {o.memo ? `: ${o.memo}` : ""}
                      </Typography>
                    </TableCell>
                    <TableCell>{o.check_number || "—"}</TableCell>
                    <TableCell align="right">{fmt(o.amount)}</TableCell>
                    <TableCell><Chip size="small" label="not an invoice" /></TableCell>
                    <TableCell>on this slip</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2, flexWrap: "wrap" }}>
            {!catchUp && (
              <TextField size="small" type="date" label="Deposit date" value={depositDate}
                         onChange={(e) => setDepositDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            )}
            <TextField size="small" label="Note (optional)" value={notes} sx={{ flex: "1 1 200px" }}
                       onChange={(e) => setNotes(e.target.value)} />
            <Typography variant="body2" sx={{ ml: "auto" }}>
              {itemCount} item{itemCount === 1 ? "" : "s"} · <b>{fmt(totalCents / 100)}</b>
              {catchUp && catchUpDiff !== 0 && (
                <span style={{ color: "#ef4444", marginLeft: 8 }}>
                  {fmt(Math.abs(catchUpDiff) / 100)} {catchUpDiff > 0 ? "over" : "short of"} the bank deposit
                </span>
              )}
            </Typography>
            <Button variant="contained" startIcon={<PrintIcon />} onClick={create}
                    disabled={creating || !itemCount || numberMissing || (catchUp && catchUpDiff !== 0)}>
              {creating ? "Creating…" : catchUp ? "Record slip" : "Create slip & print"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ background: cardBg, border: `1px solid ${border}` }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Recent slips</Typography>
          {slips.length === 0 ? (
            <Typography variant="body2" sx={{ color: muted }}>No slips yet.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Slip</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Checks</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Bank</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {slips.map((s) => (
                  <React.Fragment key={s.deposit_id}>
                    <TableRow hover>
                      <TableCell padding="checkbox">
                        <IconButton size="small" onClick={() => setExpanded(expanded === s.deposit_id ? null : s.deposit_id)}>
                          {expanded === s.deposit_id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell><b>{s.deposit_number}</b></TableCell>
                      <TableCell>{fmtDay(s.deposit_date)}</TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography variant="body2" noWrap title={(s.items || []).map((i) => i.payer_name).join(", ")}>
                          {(s.items || []).map((i) => i.payer_name).join(", ")}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{fmt(s.total_amount)}</TableCell>
                      <TableCell>{statusChip(s)}</TableCell>
                      <TableCell>{s.prepared_by}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        <Tooltip title="Print / reprint the slip">
                          <IconButton size="small" onClick={() => openSlipPdf(s.deposit_id, onShowMessage)}>
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {s.can_void && (
                          <Button size="small" color="inherit" onClick={() => { setVoidTarget(s); setVoidReason(""); }}>
                            Void
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={8} sx={{ p: 0, borderBottom: expanded === s.deposit_id ? undefined : "none" }}>
                        <Collapse in={expanded === s.deposit_id} unmountOnExit>
                          <SlipDetails depositId={s.deposit_id} onShowMessage={onShowMessage} onChanged={load} border={border} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <RecordCheckDialog
        open={recordOpen}
        onClose={() => setRecordOpen(false)}
        customers={customers}
        today={onHand?.today}
        onShowMessage={onShowMessage}
        onRecorded={(groupId) => { setRecordOpen(false); if (groupId) pendingSelect.current = groupId; load(); }}
      />
      <OtherMoneyDialog
        open={otherOpen}
        onClose={() => setOtherOpen(false)}
        customers={customers}
        categories={onHand?.other_categories}
        onAdd={(item) => { setOthers([...others, item]); setOtherOpen(false); }}
      />
      <MatchSlipsDialog
        txn={matchTarget}
        openSlips={openSlips}
        onClose={() => setMatchTarget(null)}
        onShowMessage={onShowMessage}
        onMatched={() => { setMatchTarget(null); setExpanded(null); load(); }}
      />

      <Dialog open={!!voidTarget} onClose={() => setVoidTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Void {voidTarget?.deposit_number}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Only for a slip that did NOT go to the bank (printed wrong, or the checks stayed here). Its checks go back
            on the list; a prepayment it held as credit is voided too.
          </Typography>
          <TextField fullWidth size="small" label="Why?" value={voidReason} autoFocus
                     onChange={(e) => setVoidReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidTarget(null)}>Keep it</Button>
          <Button color="error" variant="contained" onClick={confirmVoid}>Void slip</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!dismissTarget} onClose={() => setDismissTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Not customer money?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {dismissTarget ? `${fmt(dismissTarget.amount)} on ${fmtDay(dismissTarget.date)}` : ""}: an owner or loan
            transfer, a refund, anything that isn't a customer check. It leaves this list and stops alerting.
          </Typography>
          <TextField fullWidth size="small" label="Your name" value={dismissBy} sx={{ mb: 1.5 }}
                     onChange={(e) => setDismissBy(e.target.value)} />
          <TextField fullWidth size="small" label="What it was" value={dismissReason}
                     onChange={(e) => setDismissReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDismissTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={confirmDismiss}>Mark it</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
